import React, { useState, useContext } from 'react';
import { toast } from 'react-toastify';
import styles from './ContactForm.module.css';
import ThemeContext from '../../contexts/ThemeContext';

const initialFormState = {
  name: '',
  contact: '',
  email: '',
  message: '',
};

const FormInput = ({ value, name, type, placeholder, onChange, pattern, maxLength, error }) => {
  const { testimonialColors } = useContext(ThemeContext);
  const errorId = `${name}-error`;
  return (
    <div
      className={styles.inputContainer}
      style={{
        borderColor: error ? 'red' : testimonialColors.cardImageBorderColor
      }}
    >
      <input
        data-testid={name}
        required
        type={type}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        pattern={pattern}
        maxLength={maxLength}
        aria-invalid={!!error}
        aria-describedby={error ? errorId : undefined}
      />
      {/* Without this the only feedback is a red border and a disabled Submit,
          which leaves the visitor with no idea what to correct. */}
      {error && (
        <span id={errorId} role="alert" className={styles.errorText}>
          {error}
        </span>
      )}
    </div>
  );
};

const ContactForm = () => {
  const {
    secondaryFont,
    secondaryColor,
    blackColor,
    whiteColor,
    testimonialColors,
  } = useContext(ThemeContext);

  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { name, contact, email, message } = formData;

  const validateInput = (name, value) => {
    switch (name) {
      case 'name':
        if (!/^[a-zA-Z\s]{2,30}$/.test(value)) {
          return 'Name should only contain letters and spaces (2-30 characters)';
        }
        break;
      case 'contact':
        if (!/^\+?[\d\s-]{10,15}$/.test(value)) {
          return 'Please enter a valid phone number (10-15 digits)';
        }
        break;
      case 'email':
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) {
          return 'Please enter a valid email address';
        }
        break;
      case 'message':
        if (!value.trim()) {
          return 'Message cannot be empty';
        }
        break;
      default:
        return '';
    }
    return '';
  };

  const handleValueChange = (event) => {
    const { name, value } = event.target;
    const error = validateInput(name, value);
    
    setErrors({
      ...errors,
      [name]: error
    });

    setFormData({ ...formData, [name]: value });
  };

  // Netlify Forms: POST url-encoded data to the page path. The form is
  // registered at deploy time via the hidden static form in public/index.html.
  const encode = (data) =>
    Object.keys(data)
      .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`)
      .join('&');

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationErrors = {};
    Object.entries(formData).forEach(([field, value]) => {
      const error = validateInput(field, value);
      if (error) validationErrors[field] = error;
    });
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode({ 'form-name': 'contact-form', ...formData }),
      });
      if (!response.ok) {
        throw new Error(`Form submission failed with status ${response.status}`);
      }
      toast.success("Thanks for reaching out! We'll get back to you shortly.");
      setFormData(initialFormState);
      setErrors({});
    } catch (error) {
      toast.error(
        'Something went wrong sending your message. Please try again in a moment.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const secondaryFontStyle = {
    fontFamily: secondaryFont,
    color: blackColor,
  };
  const buttonStyle = {
    color: whiteColor,
    backgroundColor: secondaryColor,
  };

  return (
    <div className={styles.contactForm} data-testid="Contact-From">
      <form
        name="contact-form"
        method="post"
        data-netlify="true"
        netlify-honeypot="bot-field"
        onSubmit={handleSubmit}
        data-testid="ContactFormElement"
        style={secondaryFontStyle}
        className={styles.inputGroup}
      >
        <input type="hidden" name="form-name" value="contact-form" />
        <p hidden aria-hidden="true">
          <label>
            Don't fill this out if you're human: <input name="bot-field" />
          </label>
        </p>
        <div className={styles.formInputs}>
          <FormInput
            label="Name"
            value={name}
            name={"name"}
            type="text"
            placeholder="Name"
            onChange={handleValueChange}
            pattern="[a-zA-Z\s]{2,30}"
            maxLength={30}
            error={errors.name}
          />
          <FormInput
            label="Phone"
            value={contact}
            name={"contact"}
            type="tel"
            placeholder="Phone"
            onChange={handleValueChange}
            pattern="[\d\s-]{10,15}"
            maxLength={15}
            error={errors.contact}
          />
        </div>
        <div className={styles.emailInput}>
          <div className={styles.formInputs}>
            <FormInput
              label="Email"
              value={email}
              name={"email"}
              type="email"
              placeholder="Email"
              onChange={handleValueChange}
              error={errors.email}
            />
          </div>
        </div>
        <div className={styles.formInputs}>
          <div
            className={styles.inputTextArea}
            style={{
              borderColor: errors.message
                ? "red"
                : testimonialColors.cardImageBorderColor,
            }}
          >
            <textarea
              data-testid="message"
              required
              style={secondaryFontStyle}
              name="message"
              placeholder="How can we help you?"
              rows={10}
              value={message}
              onChange={handleValueChange}
              aria-invalid={!!errors.message}
              aria-describedby={errors.message ? 'message-error' : undefined}
            />
            {errors.message && (
              <span id="message-error" role="alert" className={styles.errorText}>
                {errors.message}
              </span>
            )}
            <button
              type="submit"
              style={buttonStyle}
              disabled={
                isSubmitting || Object.keys(errors).some((key) => errors[key])
              }
            >
              {isSubmitting ? 'Sending...' : 'Submit'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
